import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { formatTime, getScoreMessage } from '../utils/gameUtils';
import { LeaderboardService } from '../services/leaderboardService';

const GameOver = ({ gameData, gameMode, difficulty, onRestart, onBackToMenu }) => {
  const { t } = useLanguage();
  const { score, elapsedTime } = gameData;
  const message = getScoreMessage(score, 47, gameMode, difficulty, t);
  
  // Leaderboard state
  const [leaderboard, setLeaderboard] = useState([]);
  const [fullLeaderboard, setFullLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSubmissionForm, setShowSubmissionForm] = useState(true);
  const [username, setUsername] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [playerPosition, setPlayerPosition] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const gameModeKey = LeaderboardService.getGameModeKey(gameMode, difficulty);

  useEffect(() => {
    loadLeaderboard();
  }, [gameModeKey]);

  const loadLeaderboard = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await LeaderboardService.getLeaderboard(gameModeKey);
      setLeaderboard(data.entries || []);
      setFullLeaderboard(data.entries || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitScore = async (e) => {
    e.preventDefault();
    
    const validation = LeaderboardService.validateUsername(username);
    if (!validation.valid) {
      setSubmissionError(validation.error);
      return;
    }

    setSubmitting(true);
    setSubmissionError('');

    try {
      const result = await LeaderboardService.submitScore(
        gameModeKey,
        username.trim(),
        score,
        elapsedTime
      );

      setSubmissionSuccess(true);
      setPlayerPosition(result.position);
      setShowSubmissionForm(false);
      setLeaderboard(result.leaderboard || []);
      setFullLeaderboard(result.leaderboard || []);
    } catch (err) {
      if (err.message.includes('already taken')) {
        setSubmissionError(t('usernameAlreadyTaken'));
      } else {
        setSubmissionError(err.message || t('submissionError'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSeeMore = () => {
    setExpanded(!expanded);
  };

  const renderModeBadge = () => {
    if (gameMode === 'plate') {
      return (
        <span className={`difficulty-badge ${difficulty}`}>
          {t('plateMode')} - {t(difficulty)}
        </span>
      );
    } else {
      return (
        <span className={`difficulty-badge ${difficulty}`}>
          {t('codeMode')} - {t(difficulty)}
        </span>
      );
    }
  };

  const getPositionDisplay = (position) => {
    if (position === 1) return '🏆';
    if (position === 2) return '🥈';
    if (position === 3) return '🥉';
    return `#${position}`;
  };

  const formatTimeFromSeconds = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const displayedEntries = expanded ? fullLeaderboard : leaderboard.slice(0, 10);
  const hasMoreEntries = leaderboard.length > 10;

  return (
    <div className="game-over">
      <h2>{t('gameComplete')}</h2>
      <div className="final-score">{score}/47</div>
      <div className="final-stats">
        <div className="final-stat">
          <div className="final-stat-label">{t('timeLabel')}</div>
          <div className="final-stat-value">{formatTime(elapsedTime)}</div>
        </div>
        <div className="final-stat">
          <div className="final-stat-label">{t('modeLabel')}</div>
          <div className="final-stat-value">
            {renderModeBadge()}
          </div>
        </div>
      </div>
      <div className="score-message">{message}</div>

      {/* Integrated Leaderboard Section */}
      <div className="integrated-leaderboard">
        <h3>{t('leaderboard')}</h3>
        
        {/* Submission Form */}
        {showSubmissionForm && !submissionSuccess && (
          <div className="score-submission-inline">
            <p className="submission-prompt">{t('enterUsername')}</p>
            <form onSubmit={handleSubmitScore} className="inline-form">
              <div className="input-row">
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={t('usernamePlaceholder')}
                  maxLength="20"
                  disabled={submitting}
                  className="username-input-inline"
                />
                <button type="submit" disabled={submitting || !username.trim()} className="submit-btn-inline">
                  {submitting ? t('submitting') : t('submitScore')}
                </button>
                <button type="button" onClick={() => setShowSubmissionForm(false)} className="skip-btn-inline">
                  {t('skipSubmission')}
                </button>
              </div>
              <div className="username-hint-inline">{t('usernameRules')}</div>
            </form>
            
            {submissionError && (
              <div className="error-message-inline">{submissionError}</div>
            )}
          </div>
        )}

        {/* Submission Success */}
        {submissionSuccess && playerPosition && (
          <div className="submission-success-inline">
            <div className="success-message-inline">
              {t('scoreSubmitted')} {t('yourPosition')}: {getPositionDisplay(playerPosition)}
            </div>
          </div>
        )}

        {/* Leaderboard Display */}
        <div className="leaderboard-display">
          {loading && <div className="loading-inline">{t('loading')}</div>}
          
          {error && (
            <div className="error-display-inline">
              <p>{error}</p>
              <button onClick={loadLeaderboard} className="retry-btn-inline">{t('retry')}</button>
            </div>
          )}
          
          {!loading && !error && (
            <>
              {leaderboard.length === 0 ? (
                <div className="empty-leaderboard-inline">{t('noScoresYet')}</div>
              ) : (
                <>
                  <div className="leaderboard-table-inline">
                    <div className="leaderboard-header-row-inline">
                      <span>{t('rank')}</span>
                      <span>{t('player')}</span>
                      <span>{t('score')}</span>
                      <span>{t('time')}</span>
                    </div>
                    
                    {displayedEntries.map((entry, index) => (
                      <div 
                        key={`${entry.username}-${entry.timestamp}`}
                        className={`leaderboard-row-inline ${entry.username === username.trim() ? 'current-player-inline' : ''}`}
                      >
                        <span className="rank-inline">{getPositionDisplay(index + 1)}</span>
                        <span className="player-inline">{entry.username}</span>
                        <span className="score-inline">{entry.score}/47</span>
                        <span className="time-inline">{entry.timeFormatted}</span>
                      </div>
                    ))}
                  </div>
                  
                  {hasMoreEntries && (
                    <button 
                      onClick={handleSeeMore} 
                      className="see-more-btn-inline"
                    >
                      {expanded ? t('seeLess') : t('seeMore')}
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
      
      <div className="game-over-buttons">
        <button onClick={onRestart}>{t('playAgain')}</button>
        <button onClick={onBackToMenu}>{t('mainMenu')}</button>
      </div>
    </div>
  );
};

export default GameOver;